/* eslint-disable max-nested-callbacks */
import { Container, Divider } from '@mantine/core';
import { forwardRef, useMemo } from 'react';

import {
  Declarant,
  EnvironmentCountries,
  UTBReportDataDeclarant,
  UTBReportDataGroup,
  UTBReportDataImporter,
  UTBReportDataRepresentative,
} from '@e-origin/shared';

import { UTBHeader } from './UTBHeader';
import { UTBObjectionClause } from './UTBObjectionClause';
import classes from './UTBReportTemplate.module.css';
import { UTBResources } from './UTBResources';
import { UTBSubHeader } from './UTBSubHeader';
import { UTBTotal } from './UTBTotal';
import { UTBTotalPerResource } from './UTBTotalPerResource';

interface UTBReportTemplateProps {
  representative: UTBReportDataRepresentative;
  utbDeclarant: UTBReportDataDeclarant;
  importer: UTBReportDataImporter;
  group: UTBReportDataGroup;
  declarant: Declarant;
  t: (path: string, variables?: Record<string, any>) => string;
  country: string;
}

interface TaxSummary {
  taxType: string;
  total: number;
}

export const UTBReportTemplate = forwardRef(({ representative, utbDeclarant, declarant, group, importer, t, country }: UTBReportTemplateProps, ref: any) => {
  const resources = useMemo(() => {
    const taxesMap = new Map<string, TaxSummary>();
    group.declarations.forEach((declaration) => {
      declaration.calculationResultsItems.forEach((item) => {
        const currentValue = taxesMap.get(item.taxType) || {
          taxType: item.taxType,
          total: 0,
        };
        currentValue.total += item.payableTaxAmount || 0;
        taxesMap.set(item.taxType, currentValue);
      });
    });

    return Array.from(taxesMap.values());
  }, [group.declarations]);

  return (
    <>
      {/* only visible on preview */}
      <UTBHeader declarant={declarant} t={t} />
      <Container p={0} fluid ref={ref} className={classes.container}>
        <UTBSubHeader representative={representative} declarant={utbDeclarant} importer={importer} group={group} t={t} />

        {country !== EnvironmentCountries.BE && (
          <>
            <Divider />
            <UTBObjectionClause t={t} />
          </>
        )}

        {group.declarations.map((declaration, declarationIdx) => (
          <UTBResources key={declarationIdx} group={group} declaration={declaration} t={t} />
        ))}

        <UTBTotalPerResource resources={resources} t={t} />

        <UTBTotal resources={resources} t={t} />
      </Container>
    </>
  );
});
